import {collection, doc, getDocs, runTransaction, serverTimestamp} from 'firebase/firestore';

const STALE_MS = 15 * 60 * 1000;
const millis = value => typeof value?.toMillis === 'function' ? value.toMillis()
  : typeof value?.toDate === 'function' ? value.toDate().getTime() : 0;
const eventLinked = data => Boolean(data.calendarEventId || data.sessionPlanId
  || String(data.reservationKey || '').startsWith('setup:'));

export async function startReservedMatch({db, teamId, mapId, matchId, identity = null}) {
  const matchRef = doc(db, 'teams', teamId, 'maps', mapId, 'matches', matchId);
  const claimRef = identity?.sessionId ? doc(collection(matchRef, 'claims'), identity.sessionId) : null;
  return runTransaction(db, async transaction => {
    const match = await transaction.get(matchRef);
    if (!match.exists()) throw new Error('That match is no longer available. Select the event again.');
    const claim = claimRef ? await transaction.get(claimRef) : null;
    if (match.data().reservationState === 'reserved') {
      transaction.update(matchRef, {reservationState:'recording_started',
        reservationLastHeartbeatAt:serverTimestamp(), reservationStartedAt:serverTimestamp()});
    }
    // Each recorder owns only its claim. Clearing every user's claim in a batch
    // races another start and is denied once the first start changes the state.
    if (claim?.exists()) transaction.delete(claimRef);
  });
}

export async function releaseReservedMatch({db, teamId, mapId, matchId, identity, allowMatchDelete = true}) {
  const matchRef = doc(db, 'teams', teamId, 'maps', mapId, 'matches', matchId);
  const claimsRef = collection(matchRef, 'claims');
  const claimRef = doc(claimsRef, identity.sessionId);
  return runTransaction(db, async transaction => {
    const match = await transaction.get(matchRef);
    const claim = await transaction.get(claimRef);
    const data = match.data() || {};
    // Read the parent before querying claims. Every claim creation/renewal also
    // writes this parent, so a concurrent join invalidates this transaction and
    // reruns the query before an unused reservation can be deleted.
    const claims = match.exists() && data.reservationState === 'reserved' ? await getDocs(claimsRef) : null;
    if (claim?.exists()) transaction.delete(claimRef);
    if (!claims) return;
    const others = claims.docs.filter(item => item.id !== identity.sessionId);
    // Calendar slots can already point here before capture begins. Keep their
    // shared match even when the last setup closes, avoiding a dangling link.
    if (allowMatchDelete && !eventLinked(data) && !others.length && data.reservedByUid === identity.uid) {
      transaction.delete(matchRef);
    } else {
      transaction.update(matchRef, {reservationLastHeartbeatAt:serverTimestamp()});
    }
  });
}

export async function removeStaleReservedMatch({db, matchRef, now = Date.now()}) {
  return runTransaction(db, async transaction => {
    const match = await transaction.get(matchRef);
    const data = match.data() || {};
    const heartbeat = millis(data.reservationLastHeartbeatAt) || millis(data.reservedAt);
    if (!match.exists() || data.reservationState !== 'reserved' || eventLinked(data)
      || !heartbeat || now - heartbeat < STALE_MS) return false;
    const claims = await getDocs(collection(matchRef, 'claims'));
    if (claims.docs.some(claim => now - millis(claim.data().updatedAt) < STALE_MS)) return false;
    claims.docs.forEach(claim => transaction.delete(claim.ref));
    transaction.delete(matchRef);
    return true;
  });
}
